import dayjs from 'dayjs';
import { parseApiDateTime } from './parseApiDateTime';

/**
 * Backend datetime contract (UK-only deployment)
 * -----------------------------------------------
 * Django settings: ``TIME_ZONE = "Europe/London"`` and ``USE_TZ = True``.
 * DRF settings: ``DATETIME_FORMAT = "%d-%m-%Y %H:%M:%S.%fZ"``.
 *
 * The trailing ``Z`` in the format string is a LITERAL CHARACTER, not a
 * UTC marker. Django activates ``Europe/London`` for every request, so
 * ``enforce_timezone`` converts the stored UTC value to London local time
 * BEFORE ``strftime`` renders it. The resulting string therefore reflects
 * London local time even though it carries a misleading ``Z`` suffix.
 *
 * Example: a task due at 09:00 London time on 2026-05-20
 *   - DB column: 2026-05-20T08:00:00+00:00 (UTC)
 *   - Wire string: "20-05-2026 09:00:00.000000Z"  ← 09:00 is LONDON, not UTC.
 *
 * Any parser that respects the ``Z`` as UTC will produce 10:00 BST in
 * summer (BST = UTC+1), 09:00 in winter (GMT = UTC). That asymmetric
 * shift was the 2026-05 "tasks showing 1 hour ahead" report.
 *
 * KNOWN LIMITATION — BST→GMT boundary (reminders). Reminders are written
 * as local wall-clock time carrying the literal ``.0Z`` convention rather
 * than a real UTC offset. This round-trips CORRECTLY for UK users while the
 * write and the display happen in the same offset (both BST, or both GMT).
 * BUT a reminder SET while in BST for a target date AFTER the late-October
 * clock change (when display happens in GMT) will render 1 hour EARLY,
 * because the wall-clock string was captured at the BST offset and is read
 * back at the GMT offset. We are NOT patching this here: the durable fix is
 * to move reminder writes to real UTC ISO 8601 (with a true offset) as part
 * of the planned reminder consolidation, so the stored instant is unambiguous
 * regardless of the DST state at write vs read time. Until then this is a
 * cosmetic, edge-case display shift for cross-DST-boundary reminders only.
 */

/**
 * Format a local Date object as ``YYYY-MM-DDTHH:mm`` for use as the value
 * of an ``<input type="datetime-local">`` widget.
 *
 * ``datetime-local`` interprets its value as the browser's local time
 * (no timezone). Most code historically used ``d.toISOString().slice(0, 16)``,
 * but that converts the Date to UTC FIRST and then strips the ``Z``, so
 * the widget reads the result back as local — a shift of one offset in
 * either direction. In BST this means 09:00 BST in becomes 08:00 displayed.
 *
 * This helper renders the components directly from the local Date, so
 * the round-trip through the widget is identity in any timezone.
 */
export function toDatetimeLocalString(d) {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Parse a due_at / created_at / completed_at string from the Task API.
 *
 * Two backend shapes are supported:
 *   1. ISO 8601 with proper timezone offset — ``2026-05-20T09:00:00+01:00``
 *      (passed through to dayjs unchanged; dayjs handles the offset).
 *   2. The DRF DATETIME_FORMAT output — ``"20-05-2026 09:00:00.000000Z"``
 *      where the trailing ``Z`` is a LITERAL, not a UTC marker. See the
 *      module docstring above for why. We rewrite the date portion to
 *      ``YYYY-MM-DD``, drop the literal ``Z``, and parse as naive local —
 *      which is correct for UK practice users (browser TZ = Europe/London).
 *
 * @param {string|null} raw - Raw datetime string from the API.
 * @returns {dayjs.Dayjs|null} A dayjs instance, or null if input is falsy.
 */
export function parseApiDate(raw) {
    if (!raw) return null;
    // Already ISO 8601 (starts with a 4-digit year). When the backend emits
    // a real offset (e.g. "+01:00"), dayjs handles it natively.
    if (/^\d{4}-/.test(raw)) return dayjs(raw);
    // DD-MM-YYYY HH:MM:SS[.ffffff][Z] from DRF's DATETIME_FORMAT. The "Z"
    // is a LITERAL character, NOT a UTC marker. Strip it (along with the
    // microsecond fraction, which dayjs's default parser tolerates) and
    // hand a naive ISO 8601 string to dayjs — which interprets it as the
    // browser's local time. For UK practice users on UK browsers this is
    // London local, which matches what the backend emitted.
    const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}:\d{2}:\d{2})(\.\d+)?Z?$/);
    if (match) {
        const [, dd, mm, yyyy, time, frac] = match;
        return dayjs(`${yyyy}-${mm}-${dd}T${time}${frac || ''}`);
    }
    // Fallback — let dayjs try its best.
    return dayjs(raw);
}

/**
 * Canonical display formatter for ALL task / checklist API dates.
 *
 * ALWAYS use this (or parseApiDate) for an API date — NEVER `new Date(apiString)`.
 * The backend emits two formats: DRF "DD-MM-YYYY HH:mm:ss.SSSSSSZ" (most
 * serializers — paused_until, completed_at, the /tasks/ list) and ISO
 * "YYYY-MM-DDTHH:mm:ss" (the hand-built task-checklists due_at). `new Date()`
 * only parses ISO, so it renders "Invalid Date" on every DRF value. parseApiDate
 * auto-detects both; this wraps it and guards against the invalid case.
 *
 * @param {string|null} raw     API date string (either format).
 * @param {string}      pattern dayjs format token (default "DD MMM" -> "18 Jun").
 * @returns {string|null} the formatted label, or null when missing/unparseable.
 */
export function formatTaskDate(raw, pattern = 'DD MMM') {
    const d = parseApiDate(raw);
    return d && d.isValid() ? d.format(pattern) : null;
}

/**
 * Domain-neutral alias of {@link formatTaskDate}, defaulting to a full
 * date + time label.
 *
 * `formatTaskDate` is named after the module it grew up in, but the wire
 * contract it implements (see the module docstring) is global — EVERY DRF
 * serializer in this backend emits `DD-MM-YYYY HH:mm:ss.SSSSSSZ`, not just
 * the Tasks ones. Non-task callers were reaching for `new Date(apiString)`
 * instead, which yields `Invalid Date` on that format; `fDateTime` in
 * `utils/formatTime.js` then swallows it and renders an empty string. That
 * is how the WhatsApp archived-lead banner shipped reading
 * "They messaged again on ." with no date at all (ISS-025B).
 *
 * Use this from any non-task surface so the intent reads correctly at the
 * call site. Behaviour is identical — same parser, same literal-`Z`
 * handling, same null-on-unparseable contract.
 *
 * @param {string|null} raw     API date string (either format).
 * @param {string}      pattern dayjs format token.
 * @returns {string|null} the formatted label, or null when missing/unparseable.
 */
export function formatApiDateTime(raw, pattern = 'DD MMM YYYY, HH:mm') {
    return formatTaskDate(raw, pattern);
}

/**
 * Parse an API date into a **moment** object (for moment-based MUI date pickers
 * and any code that needs `.format()`/comparison via moment). Handles BOTH the
 * DRF "DD-MM-YYYY HH:mm:ss.SSSSSSZ" format and ISO — NEVER use bare
 * `moment(apiString)`, which renders "Invalid date" on the DRF format because
 * moment can't infer it without an explicit format token.
 *
 * Delegates to `parseApiDateTime` (src/utils/parseApiDateTime.js). This used to
 * hand moment an explicit format string ending in a bare `Z` token
 * (`'DD-MM-YYYY HH:mm:ss.SSSSSSZ'`) to parse the DRF shape — but `Z` in a
 * moment format string means "parse a real UTC offset here", not "match a
 * literal Z character". Since the BE's trailing `Z` is a misleading literal
 * (TIME_ZONE=Europe/London, so the value is UK wall-clock, not UTC), moment
 * silently treated it as a genuine `+00:00` offset and shifted the result an
 * hour into the future during BST — the same "in an hour" root cause as the
 * Timeline/Overview bug documented in `parseApiDateTime`'s header. `parseApiDateTime`
 * treats that `Z` as the literal it is and reads the value as UK local time instead.
 *
 * @param {string|null} raw API date string (either format).
 * @returns {import('moment').Moment|null} a valid moment, or null when missing/unparseable.
 */
export function momentFromApi(raw) {
    return parseApiDateTime(raw);
}

/**
 * Derive the **Europe/London calendar date** of a Reminder datetime string
 * returned by the API, as a "YYYY-MM-DD" string suitable for lexicographic
 * bucket comparison with `londonDateStr`.
 *
 * ## Why this is needed
 *
 * The DRF `DATETIME_FORMAT` is `"%d-%m-%Y %H:%M:%S.%fZ"` (see settings.py),
 * so the wire value is e.g. `"30-06-2026 09:00:00.000000Z"`.  `slice(0,10)`
 * gives `"30-06-2026"` (DD-MM-YYYY), not the `"2026-06-30"` (YYYY-MM-DD)
 * that `londonDateStr` returns — string comparison would be meaningless.
 *
 * The date portion of the DRF string is already the **London calendar date**
 * (Django activates `Europe/London` for every request, renders the instant in
 * local time, then appends a literal `Z` — see module docstring above and
 * `src/utils/smartReminderRules.js:9`).  So we only need to **reformat**
 * DD-MM-YYYY → YYYY-MM-DD; we must NOT parse through a UTC Date or moment
 * local-mode, which would re-apply the London→UTC offset and shift the date.
 *
 * Also accepts ISO 8601 strings (`"2026-06-30T09:00:00Z"`) for test
 * convenience — those are returned by mock fixtures and the BST-boundary
 * tests.  When the string already starts with a 4-digit year it is treated as
 * ISO and the leading 10 chars are already YYYY-MM-DD.
 *
 * @param {string|null} raw  API datetime string (DRF DD-MM-YYYY or ISO 8601).
 * @returns {string|null}    "YYYY-MM-DD" London calendar date, or null if unparseable.
 */
export function londonDateStrFromApi(raw) {
    if (!raw) return null;
    // ISO 8601: already starts with "YYYY-" → leading 10 chars are YYYY-MM-DD.
    if (/^\d{4}-/.test(raw)) return raw.slice(0, 10);
    // DRF DD-MM-YYYY HH:mm:ss.ffffffZ → reformat to YYYY-MM-DD.
    const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (match) {
        const [, dd, mm, yyyy] = match;
        return `${yyyy}-${mm}-${dd}`;
    }
    return null;
}

/**
 * Return the calendar date in the **Europe/London** timezone as a "YYYY-MM-DD"
 * string, optionally shifted by `deltaDays`.
 *
 * Must match the BE bucket logic in `apps/tasks/new_lead_task_buckets.py`
 * which uses `django.utils.timezone.localdate()` with
 * ``TIME_ZONE = "Europe/London"``.  Computed via `Intl` so it is DST-safe —
 * the noon-anchor approach prevents the shift from ever landing on the wrong
 * calendar day (same pattern as `londonDateShifted` in taskThunks.js).
 *
 * The optional `now` parameter is for unit tests — pass a fixed `Date` to
 * make date-boundary tests deterministic.
 *
 * @param {number} [deltaDays=0]  Offset in whole days (+N=future, -N=past).
 * @param {Date}   [now=new Date()] Reference instant (default: wall-clock).
 * @returns {string} "YYYY-MM-DD" in Europe/London.
 */
export function londonDateStr(deltaDays = 0, now = new Date()) {
    // Derive the London calendar date of `now` via Intl ('en-CA' locale gives
    // "YYYY-MM-DD" ordering without any extra parsing).
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(now);
    const [y, m, d] = parts.split('-').map(Number);
    // Use a UTC-noon anchor for the shift arithmetic — adding whole days at noon
    // never falls into a DST gap/overlap and never crosses into an adjacent day.
    const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    anchor.setUTCDate(anchor.getUTCDate() + deltaDays);
    const pad = (n) => String(n).padStart(2, '0');
    return `${anchor.getUTCFullYear()}-${pad(anchor.getUTCMonth() + 1)}-${pad(anchor.getUTCDate())}`;
}
